import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { ReactNode } from 'react';
import { toObsContent } from '~/components/Content/Content.hooks';
import { BaseError } from '~/components/Error/BaseError';
import { getResolvedLanguage } from '~/components/I18n';
import { T } from '~/components/I18n/Translation';
import { NftModel, SaleModel } from '~/components/Nft/Nft.hooks';
import { UseDosiminQueryOptions } from '~/components/Query/Query.hooks';

export type StoreSaleNftsParams = {
  size?: number;
};

export type SaleStatus = 'UPCOMING' | 'FOR_SALE' | 'SOLD_OUT' | 'CLOSED';

// https://wiki.linecorp.com/display/GLONFT/02.+store+explore+API+-+V1.4.0#:~:text=%7D-,sales%20list,-(GET)/stores/%7BstoreName
export type StoreSaleNftModel = NftModel &
  SaleModel & {
    brandId: number;
    category: number;
    saleStatus: SaleStatus;
    upcomingAt: Date;
    openAt: Date;
    closeAt: Date;
    isPrivateSale: boolean;
    galleryId?: number;
    secondaryCurrency: string;
    partnerKey: string;
    openAlarmRegistered: boolean;
    donReward: number;
  };

export type StoreSaleNftResponseModel = Omit<StoreSaleNftModel, 'title' | 'description'> & {
  title: Record<string, string>;
  description: Record<string, string>;
};

export const saleStatusMap: Record<SaleStatus, ReactNode> = {
  FOR_SALE: <T name="citizen_b2c_status_forsale" />,
  SOLD_OUT: <T name="citizen_b2c_status_soldout" />,
  UPCOMING: <T name="citizen_b2c_status_upcoming" />,
  CLOSED: <T name="citizen_b2c_status_endsale" />,
};

export type StoreSaleNftsResponse = {
  responseData: StoreSaleNftResponseModel[];
};

export const storeSaleNftsUrl = '/api/stores/citizen/v2/sales/home';

export const dirtyLocalize = (
  object: Record<string, string>,
  lang: string = getResolvedLanguage()
) => {
  const keys = Object.keys(object);
  const hasExactKey = keys.includes(lang);
  const key = hasExactKey ? lang : keys.find((key) => key.includes(lang)) || 'en-US';
  return object[key];
};
export const transform = ({
  title,
  description,
  upcomingAt,
  openAt,
  closeAt,
  ...rest
}: StoreSaleNftResponseModel) => {
  const localTitle = dirtyLocalize(title);
  const localDescription = dirtyLocalize(description);
  return {
    ...rest,
    ...toObsContent(rest, { thumbnailId: 'w600' }),
    title: localTitle,
    description: localDescription,
    upcomingAt: new Date(upcomingAt),
    openAt: new Date(openAt),
    closeAt: new Date(closeAt),
  };
};

export const getStoreSaleNfts = async (params?: StoreSaleNftsParams) => {
  const {
    data: { responseData },
  } = await axios.get<StoreSaleNftsResponse>(storeSaleNftsUrl, { params });
  if (responseData.length === 0) {
    throw new BaseError('EMPTY');
  }
  return responseData.map(transform);
};

export const useStoreNfts = (
  params?: StoreSaleNftsParams,
  options?: UseDosiminQueryOptions<StoreSaleNftModel[], BaseError>
) =>
  useQuery<StoreSaleNftModel[], BaseError>(
    ['storeSaleNfts', params],
    () => getStoreSaleNfts(params),
    {
      refetchOnWindowFocus: false,
      initialData: [],
      ...options,
    }
  );
